/**
 * DocumentLifecycleService.js — Suppression et archivage sûrs des documents.
 *
 * ⚠️ FICHIER PARTAGÉ : une copie identique existe dans MyConcours-web/services/.
 * Toute modification doit être répercutée dans les DEUX projets.
 *
 * ---------------------------------------------------------------------------
 * RÈGLE MÉTIER
 * ---------------------------------------------------------------------------
 * Un document acheté ne doit JAMAIS être supprimé physiquement : l'utilisateur
 * a payé, il conserve son accès à vie. L'admin peut le retirer du catalogue
 * (archivage), pas l'effacer.
 *
 *   0 achat   → suppression réelle autorisée
 *   N achats  → archivage uniquement (archived: true)
 *
 * L'archivage est réversible et n'entraîne aucune perte de données.
 */

import {
  collection, query, where, getDocs, getDoc, doc,
  deleteDoc, updateDoc, serverTimestamp, limit,
} from 'firebase/firestore';
import { db } from '../private/firebaseConfig';

const collectionForType = (type) =>
  type === 'concours' ? 'concours' : type === 'bords' ? 'bords' : 'documents';

const DocumentLifecycleService = {

  /**
   * Compte les achats confirmés portant sur un document.
   * Sert à décider entre suppression réelle et archivage.
   */
  countPurchases: async (documentId) => {
    try {
      const snapshot = await getDocs(query(
        collection(db, 'purchases'),
        where('documentId', '==', documentId),
        where('status', '==', 'completed')
      ));
      return snapshot.size;
    } catch (error) {
      console.error('Error counting purchases:', error);
      // En cas d'erreur on renvoie -1 : l'appelant DOIT traiter ce cas comme
      // « impossible de garantir qu'il n'y a pas d'achat » et refuser la
      // suppression. Ne jamais renvoyer 0 ici : ce serait autoriser une
      // suppression destructrice sur une simple panne réseau.
      return -1;
    }
  },

  /**
   * Vérifie si un document peut être supprimé physiquement.
   * @returns {{canDelete: boolean, purchaseCount: number, reason: string}}
   */
  checkDeletionSafety: async (documentId) => {
    const purchaseCount = await DocumentLifecycleService.countPurchases(documentId);

    if (purchaseCount < 0) {
      return {
        canDelete: false,
        purchaseCount: 0,
        reason: 'Impossible de vérifier les achats liés à ce document. '
              + 'Vérifiez votre connexion et réessayez.',
      };
    }
    if (purchaseCount > 0) {
      return {
        canDelete: false,
        purchaseCount,
        reason: `${purchaseCount} utilisateur${purchaseCount > 1 ? 's ont' : ' a'} acheté `
              + `ce document. Il sera masqué du catalogue mais restera accessible `
              + `à ${purchaseCount > 1 ? 'ces acheteurs' : 'cet acheteur'}.`,
      };
    }
    return { canDelete: true, purchaseCount: 0, reason: '' };
  },

  /**
   * Retire un document du catalogue sans détruire l'accès des acheteurs.
   */
  archiveDocument: async (documentId, documentType, adminId = null) => {
    try {
      await updateDoc(doc(db, collectionForType(documentType), documentId), {
        archived: true,
        archivedAt: serverTimestamp(),
        ...(adminId ? { archivedBy: adminId } : {}),
        status: 'inactive',
      });
      return { success: true };
    } catch (error) {
      console.error('Error archiving document:', error);
      return { success: false, error: error.message };
    }
  },

  /** Remet un document archivé dans le catalogue. */
  restoreDocument: async (documentId, documentType) => {
    try {
      await updateDoc(doc(db, collectionForType(documentType), documentId), {
        archived: false,
        archivedAt: null,
        status: 'active',
      });
      return { success: true };
    } catch (error) {
      console.error('Error restoring document:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Suppression sûre : supprime si aucun achat, archive sinon.
   * C'est le SEUL point d'entrée que les écrans admin doivent utiliser.
   *
   * @returns {{action:'deleted'|'archived'|'failed', purchaseCount:number, message:string}}
   */
  safeDelete: async (documentId, documentType, adminId = null) => {
    const safety = await DocumentLifecycleService.checkDeletionSafety(documentId);

    if (safety.canDelete) {
      try {
        await deleteDoc(doc(db, collectionForType(documentType), documentId));
        return {
          action: 'deleted',
          purchaseCount: 0,
          message: 'Document supprimé définitivement.',
        };
      } catch (error) {
        console.error('Error deleting document:', error);
        return { action: 'failed', purchaseCount: 0, message: 'Suppression impossible.' };
      }
    }

    // Panne de vérification → on ne touche à rien.
    if (safety.purchaseCount === 0) {
      return { action: 'failed', purchaseCount: 0, message: safety.reason };
    }

    const result = await DocumentLifecycleService.archiveDocument(documentId, documentType, adminId);
    if (!result.success) {
      return { action: 'failed', purchaseCount: safety.purchaseCount, message: 'Archivage impossible.' };
    }
    return {
      action: 'archived',
      purchaseCount: safety.purchaseCount,
      message: `Document archivé. ${safety.purchaseCount} acheteur${safety.purchaseCount > 1 ? 's conservent' : ' conserve'} son accès.`,
    };
  },

  /**
   * Vérifie qu'une épreuve précise d'un concours peut être retirée.
   * Une épreuve vendue ne doit pas disparaître de la structure du concours.
   *
   * @returns {{canRemove:boolean, purchaseCount:number, message:string}}
   */
  checkEpreuveRemoval: async (documentId, epreuve) => {
    try {
      const constraints = [
        where('documentId', '==', documentId),
        where('status', '==', 'completed'),
      ];
      const snapshot = await getDocs(query(collection(db, 'purchases'), ...constraints));

      const epreuveId = epreuve?.epreuveId || epreuve?.id;
      const matches = snapshot.docs.filter((d) => {
        const p = d.data();
        if (epreuveId && p.epreuveId) return p.epreuveId === epreuveId;
        // Repli textuel pour les achats antérieurs à l'epreuveId.
        return p.titre === epreuve?.titre
            && p.annee?.toString() === epreuve?.annee?.toString()
            && p.filiere === epreuve?.filiere;
      });

      if (matches.length > 0) {
        return {
          canRemove: false,
          purchaseCount: matches.length,
          message: `Cette épreuve a été achetée par ${matches.length} utilisateur${matches.length > 1 ? 's' : ''}. `
                 + `La retirer leur ferait perdre l'accès. Remplacez plutôt le fichier PDF.`,
        };
      }
      return { canRemove: true, purchaseCount: 0, message: '' };
    } catch (error) {
      console.error('Error checking epreuve removal:', error);
      return {
        canRemove: false,
        purchaseCount: 0,
        message: 'Impossible de vérifier les achats. Réessayez.',
      };
    }
  },

  /**
   * Vérifie si le DERNIER document d'un bord peut être retiré.
   *
   * Un bord est vendu comme un tout : l'acheteur paie l'accès à son document
   * principal (documents[0]). Retirer le dernier document laisse un bord vide
   * et prive TOUS les acheteurs de ce qu'ils ont payé.
   *
   * Retirer un document quand il en reste d'autres est autorisé : le bord
   * conserve un contenu accessible.
   *
   * @param {string} bordId          Identifiant du bord.
   * @param {number} remainingCount  Nombre de documents APRÈS le retrait.
   * @returns {{canRemove:boolean, purchaseCount:number, message:string}}
   */
  checkBordDocumentRemoval: async (bordId, remainingCount) => {
    if (remainingCount > 0) {
      return { canRemove: true, purchaseCount: 0, message: '' };
    }

    const purchaseCount = await DocumentLifecycleService.countPurchases(bordId);

    if (purchaseCount < 0) {
      return {
        canRemove: false,
        purchaseCount: 0,
        message: 'Impossible de vérifier les achats liés à ce bord. '
               + 'Vérifiez votre connexion et réessayez.',
      };
    }
    if (purchaseCount > 0) {
      return {
        canRemove: false,
        purchaseCount,
        message: `${purchaseCount} utilisateur${purchaseCount > 1 ? 's ont' : ' a'} acheté `
               + `ce bord. Retirer son dernier document leur ferait perdre l'accès à `
               + `un contenu payé. Remplacez plutôt le fichier PDF.`,
      };
    }
    return { canRemove: true, purchaseCount: 0, message: '' };
  },

  /** Un document archivé reste lisible par ses acheteurs. */
  isDocumentAccessible: async (documentId, documentType) => {
    try {
      const snap = await getDoc(doc(db, collectionForType(documentType), documentId));
      return snap.exists();
    } catch {
      return false;
    }
  },
};

export default DocumentLifecycleService;
